// §2.4 / F4 — in-place "New document request" popup for the entity-surface
// Review & requests tab. Replays CollectionPackageNewPage's savePackage
// sequence (create → addItem per template → send → invitation) as ONE
// mutation so staff never leave the entity page.
//
// Adaptive two-step flow: Step 1 picks templates/bundles + due date; when a
// selection needs staff-entered details — a special form (PR-1) or a legacy
// sender-field template — Step 2 mounts the SpecialFormEditor / sender-field
// entry per item (merge fields prefilled from the special-form definition
// endpoint, live PDF preview, full body edit) before Send. Otherwise Send is
// single-step. The captured (savedFormDataJson, bodyMarkdownOverride) ride
// each item's create.
//
// Filing (C1): the popup carries NO destination UI. The package owner is the
// hosting entity's own record (the `owner` prop), so review-time filing
// (SuggestDestinationAsync + quick-picks + folder picker) always has a valid
// default; all destination control — including drilling into linked deal /
// loan folders — happens at file time.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Search, Send } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { TemplateKindBadge } from "@/components/Collection/TemplateKindBadge";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  collectionPackagesApi,
  externalRecipientsApi,
  portalInvitationsApi,
  type CollectionPackageDto,
  type ExternalRecipientDto,
} from "@/api/collectionPackages";
import {
  collectionTemplatesApi,
  formatAudiences,
  type CollectionTemplateDto,
  type MaterialAudience,
} from "@/api/collectionTemplates";
import {
  collectionBundlesApi,
  type CollectionTemplateBundleDto,
} from "@/api/collectionBundles";
import type { BrokerType } from "@/api/brokers";
import { portalAccountsApi } from "@/api/portalAccounts";
import {
  collectionSpecialFormsApi,
  type SpecialFormDefinition,
} from "@/api/collectionSpecialForms";
import {
  attachmentToOwnerType,
  firstCompatibleOwner,
} from "@/components/Collection/portalAccountOwner";
import {
  SenderDetailsSection,
  templateHasSenderFields,
  type SenderValues,
} from "@/components/Collection/SenderDetailsSection";
import { SpecialFormEditor } from "@/components/Collection/SpecialFormEditor";
import { useOptionalAuth } from "@/hooks/useAuth";
import { collectionStagingKeys } from "@/api/collectionStagingKeys";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { calendarDateInputToIso } from "@/utils/datetime";
import { ApprovalChainComposerPanel } from "@/components/Collection/ApprovalChain/ApprovalChainComposerPanel";
import {
  SendBlockedList,
  sendBlockedProblems,
  useChainReadiness,
} from "@/components/Collection/ApprovalChain/chainSend";
import { invalidateApprovalChainReads } from "@/hooks/collection/approvalChainKeys";

export interface NewDocumentRequestModalProps {
  open: boolean;
  onClose: () => void;
  /** Party type — rides the invitation issue (the provisioning account type),
   *  exactly like the composer's party-role field. Also seeds the DEFAULT
   *  catalog audience — except a Junior broker (see `brokerType`), whose
   *  default catalog is `JuniorBroker` while provisioning stays `Broker`. */
  accountType: string;
  /** Broker sub-type, supplied only by the broker mount. A `"Junior"` broker
   *  has `accountType === "Broker"` but its default catalog should be
   *  `JuniorBroker`-tagged, not generic-`Broker`. Undefined elsewhere →
   *  behaviour unchanged. NEVER affects how the account is provisioned. */
  brokerType?: BrokerType;
  /** The account's email; drives find-or-create of the ExternalRecipient. */
  email?: string | null;
  /** portalUserId-scoped mounts pass the account id so the modal can resolve
   *  a fullName for the 404→create recipient arm (and a last-resort filing
   *  owner from the account's attachments). */
  portalUserId?: string | null;
  /** The hosting entity's OWN record — becomes the created package's owner
   *  (the review-time filing default). Every entity-surface mount supplies
   *  it, so a send never blocks on destination resolution. */
  owner?: { ownerEntityType: string; ownerEntityId: string } | null;
  /** Legacy owner-scoped mounts pass their scope; `owner` supersedes it. */
  fallbackOwner?: { ownerEntityType: string; ownerEntityId: string } | null;
  /** Fired after the whole sequence succeeded (package already sent). */
  onSent?: (pkg: CollectionPackageDto) => void;
}

/** Per-template staff-entered capture from Step 2. */
interface DetailsCapture {
  valuesJson: string | null;
  bodyOverride: string | null;
}

function normalizeOwner(
  o: { ownerEntityType: string; ownerEntityId: string } | null | undefined,
): { ownerEntityType: string; ownerEntityId: string } | null {
  if (!o) return null;
  return {
    // Normalize "LegalEntity" → the composer's "Entity" wire name; unknown
    // types (e.g. "Borrower"/"Seller"/"Brokerage"/"Tps") pass through as-is.
    ownerEntityType: attachmentToOwnerType(o.ownerEntityType) ?? o.ownerEntityType,
    ownerEntityId: o.ownerEntityId,
  };
}

export function NewDocumentRequestModal({
  open,
  onClose,
  accountType,
  brokerType,
  email,
  portalUserId,
  owner,
  fallbackOwner,
  onSent,
}: NewDocumentRequestModalProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const hasPermission = (perm: string) => auth?.hasPermission?.(perm) ?? false;
  // The DEFAULT catalog audience is distinct from the provisioning accountType:
  // a Junior broker is provisioned as a `Broker` account (below, unchanged) but
  // its default catalog is the `JuniorBroker`-tagged content. Universal search
  // (audience = null) escapes the default filter entirely.
  const catalogAudience: MaterialAudience | null =
    accountType === "Broker" && brokerType === "Junior"
      ? "JuniorBroker"
      : ((accountType || null) as MaterialAudience | null);

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [step, setStep] = useState<1 | 2 | 3>(1);
  // Approval chains (D3/D4) — a selection that routes through a chain is saved
  // as a draft first; step 3 reviews each chain (preflight, attestations,
  // adjustments) and sends. A 422 send refusal lists every problem.
  const [routingDraft, setRoutingDraft] = useState<CollectionPackageDto | null>(null);
  const [sendProblems, setSendProblems] = useState<string[]>([]);
  const chainReadiness = useChainReadiness();
  const [captures, setCaptures] = useState<Record<string, DetailsCapture>>({});
  const [search, setSearch] = useState("");
  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;

  // Same audience-narrowed lists (and cache entries) as the composer page.
  const templatesQuery = useQuery({
    queryKey: portalAbsorptionKeys.composerTemplates(catalogAudience),
    queryFn: () => collectionTemplatesApi.list(false, catalogAudience),
    enabled: open,
  });
  const bundlesQuery = useQuery({
    queryKey: portalAbsorptionKeys.composerBundles(catalogAudience),
    queryFn: () => collectionBundlesApi.list(false, catalogAudience),
    enabled: open,
  });
  const templates = templatesQuery.data ?? [];
  const bundles = bundlesQuery.data ?? [];

  // Universal search — the WHOLE active catalog (audience = null) so staff can
  // reach off-audience content. There is no server-side audience guard on the
  // send path, so any surfaced item sends freely. Fetched only while a search
  // string is present; the cached result keeps a search-selected item
  // resolvable after the search is cleared (see the union memos below).
  const searchTemplatesQuery = useQuery({
    queryKey: portalAbsorptionKeys.composerTemplates(null),
    queryFn: () => collectionTemplatesApi.list(false, null),
    enabled: open && searchActive,
  });
  const searchBundlesQuery = useQuery({
    queryKey: portalAbsorptionKeys.composerBundles(null),
    queryFn: () => collectionBundlesApi.list(false, null),
    enabled: open && searchActive,
  });
  const searchTemplatesAll = searchTemplatesQuery.data ?? [];
  const searchBundlesAll = searchBundlesQuery.data ?? [];

  // Name-matched (case-insensitive substring), active-only — what the search
  // panel renders.
  const searchTemplateMatches = useMemo(
    () =>
      searchActive
        ? searchTemplatesAll.filter(
            (t) => t.active && t.name.toLowerCase().includes(searchTerm),
          )
        : [],
    [searchActive, searchTemplatesAll, searchTerm],
  );
  const searchBundleMatches = useMemo(
    () =>
      searchActive
        ? searchBundlesAll.filter(
            (b) => b.active && b.name.toLowerCase().includes(searchTerm),
          )
        : [],
    [searchActive, searchBundlesAll, searchTerm],
  );

  // Union of the default (audience-scoped) lists with the search catalog, keyed
  // by id — so a bundle/template picked via search still expands and resolves
  // in the send sequence even after the search box is cleared.
  const allTemplates = useMemo(() => {
    const map = new Map<string, CollectionTemplateDto>();
    for (const t of templates) map.set(t.id, t);
    for (const t of searchTemplatesAll) if (!map.has(t.id)) map.set(t.id, t);
    return [...map.values()];
  }, [templates, searchTemplatesAll]);
  const allBundles = useMemo(() => {
    const map = new Map<string, CollectionTemplateBundleDto>();
    for (const b of bundles) map.set(b.id, b);
    for (const b of searchBundlesAll) if (!map.has(b.id)) map.set(b.id, b);
    return [...map.values()];
  }, [bundles, searchBundlesAll]);

  // Account detail (fullName for the 404→create recipient arm + attachments
  // as the last-resort owner) — hoisted so Step 2 can show the recipient name.
  const accountQuery = useQuery({
    queryKey: portalAbsorptionKeys.accountDetail(portalUserId ?? ""),
    queryFn: () => portalAccountsApi.get(portalUserId!),
    enabled: open && Boolean(portalUserId),
  });
  const account = accountQuery.data ?? null;

  const templateById = useMemo(() => {
    const map = new Map<string, CollectionTemplateDto>();
    for (const t of allTemplates) map.set(t.id, t);
    return map;
  }, [allTemplates]);

  // Bundles expand client-side to their ordered slot templateIds (mirrors
  // the composer's addSelectedBundles), then individually picked templates
  // append; de-duped preserving first occurrence. Resolved against the UNION
  // so a search-picked (off-audience) bundle still expands.
  const templateIdsToCreate = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (id: string) => {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    };
    for (const bundleId of selectedBundleIds) {
      const bundle = allBundles.find((b) => b.id === bundleId);
      if (!bundle) continue;
      bundle.slots
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach((s) => push(s.templateId));
    }
    for (const id of selectedTemplateIds) push(id);
    return out;
  }, [selectedBundleIds, selectedTemplateIds, allBundles]);

  // Selections needing the Step-2 details pass: special forms (PR-1 flag,
  // full editor with body edit) and legacy sender-field templates (merge
  // fields only — belt-and-braces for an unflagged template so a letter is
  // never quick-sent with every token blanked).
  const detailTemplates = useMemo(
    () =>
      templateIdsToCreate
        .map((id) => templateById.get(id))
        .filter((t): t is CollectionTemplateDto => t != null)
        .filter((t) => t.isSpecialForm || templateHasSenderFields(t.formSchemaJson)),
    [templateIdsToCreate, templateById],
  );
  const needsDetailsStep = detailTemplates.length > 0;
  const hasChainTemplate = templateIdsToCreate.some(
    (id) => templateById.get(id)?.approvalChain != null,
  );

  // Server-resolved merge-field prefill per selected special form. Party
  // context comes from the hosting entity when it's a Borrower/Seller party
  // record; a per-template failure degrades to client-side prefill only.
  const specialFormIds = useMemo(
    () => detailTemplates.filter((t) => t.isSpecialForm).map((t) => t.id),
    [detailTemplates],
  );
  const ownerForParty = normalizeOwner(owner);
  const partyId =
    ownerForParty &&
    (ownerForParty.ownerEntityType === "Borrower" ||
      ownerForParty.ownerEntityType === "Seller")
      ? ownerForParty.ownerEntityId
      : null;
  // A deal-owned request resolves the deal's party tokens (SellerName,
  // BuyerName, BrokerName on the Conditional Approval letters) server-side.
  const ownerDealId =
    ownerForParty && ownerForParty.ownerEntityType === "Deal" ? ownerForParty.ownerEntityId : null;
  const definitionsQuery = useQuery({
    queryKey: portalAbsorptionKeys.composerSpecialFormDefinitions(
      specialFormIds.join(","),
      partyId,
      ownerDealId,
    ),
    queryFn: async () => {
      const results = await Promise.all(
        specialFormIds.map(async (id) => {
          try {
            return await collectionSpecialFormsApi.getDefinition(id, { partyId, dealId: ownerDealId });
          } catch {
            return null;
          }
        }),
      );
      const map: Record<string, SpecialFormDefinition | null> = {};
      specialFormIds.forEach((id, i) => {
        map[id] = results[i];
      });
      return map;
    },
    enabled: open && step === 2 && specialFormIds.length > 0,
  });

  const initialValuesFor = (templateId: string): SenderValues | undefined => {
    const def = definitionsQuery.data?.[templateId];
    if (!def) return undefined;
    const seed: SenderValues = {};
    for (const f of def.fields) {
      if (f.prefilledValue != null && f.prefilledValue !== "") {
        seed[f.name] = f.prefilledValue;
      }
    }
    return Object.keys(seed).length > 0 ? seed : undefined;
  };

  const setCaptureValues = (templateId: string, valuesJson: string) =>
    setCaptures((prev) => ({
      ...prev,
      [templateId]: { valuesJson, bodyOverride: prev[templateId]?.bodyOverride ?? null },
    }));
  const setCaptureBody = (templateId: string, bodyOverride: string | null) =>
    setCaptures((prev) => ({
      ...prev,
      [templateId]: { valuesJson: prev[templateId]?.valuesJson ?? null, bodyOverride },
    }));

  const resetAndClose = () => {
    setSelectedTemplateIds([]);
    setSelectedBundleIds([]);
    setDueDate("");
    setStep(1);
    setCaptures({});
    setSearch("");
    setRoutingDraft(null);
    setSendProblems([]);
    onClose();
  };

  const sendMut = useMutation<CollectionPackageDto, Error, { routing: boolean } | void>({
    mutationFn: async (vars) => {
      const routing = Boolean(vars && vars.routing);
      const trimmedEmail = (email ?? "").trim();
      if (!trimmedEmail) {
        throw new Error(
          "This account has no email on file — use the full composer instead.",
        );
      }
      if (templateIdsToCreate.length === 0) {
        throw new Error("Pick at least one item to request.");
      }

      let recipient: ExternalRecipientDto;
      try {
        recipient = await externalRecipientsApi.findByEmail(trimmedEmail);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404 && account) {
          recipient = await externalRecipientsApi.create({
            fullName: account.fullName,
            email: trimmedEmail,
            phone: null,
            linkedCounterpartyType: null,
            linkedCounterpartyId: null,
          });
        } else {
          throw err;
        }
      }

      // C1 — the hosting entity's own record is the package owner (there is
      // always a valid owner, so review-time filing always has its default);
      // legacy fallbacks kept for owner-scoped/attachment-only callers.
      const resolvedOwner =
        normalizeOwner(owner) ??
        normalizeOwner(fallbackOwner) ??
        (account ? firstCompatibleOwner(account.attachments) : null);
      if (!resolvedOwner) {
        // Programming-error guard, not a data gate — every entity-surface
        // mount supplies `owner`.
        throw new Error("Missing filing owner — reopen this request from the record page.");
      }

      const created = await collectionPackagesApi.create({
        ownerEntityType: resolvedOwner.ownerEntityType,
        ownerEntityId: resolvedOwner.ownerEntityId,
        recipientId: recipient.id,
        recipientRoleLabel: null,
        dueDate: calendarDateInputToIso(dueDate),
      });

      // Anything failing past this point leaves a real Draft/Sent package —
      // say so honestly instead of pretending nothing happened.
      try {
        for (const templateId of templateIdsToCreate) {
          const capture = captures[templateId];
          await collectionPackagesApi.addItem(
            created.id,
            templateId,
            capture?.valuesJson ?? null,
            capture?.bodyOverride ?? null,
          );
        }
        if (routing) {
          // Not sent yet — step 3 reviews the chain, then sends.
          return await collectionPackagesApi.get(created.id);
        }
        await collectionPackagesApi.send(created.id);
        await portalInvitationsApi.issue(
          created.id,
          accountType as MaterialAudience,
        );
      } catch (err) {
        throw new Error(
          `Package created but sending failed — find it in Collection > Packages. (${extractErrorMessage(err)})`,
        );
      }
      return created;
    },
    onSuccess: (pkg, vars) => {
      if (vars && vars.routing) {
        void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
        void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
        invalidateApprovalChainReads(qc);
        setRoutingDraft(pkg);
        setStep(3);
        return;
      }
      toast.success("Document request sent — invitation emailed.");
      void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
      void qc.invalidateQueries({
        queryKey: portalAbsorptionKeys.accountPackagesPrefix,
      });
      onSent?.(pkg);
      resetAndClose();
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
      // A failure past create leaves a real package behind — refresh the
      // tab's reads so it shows up instead of hiding for the staleTime.
      void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
      void qc.invalidateQueries({
        queryKey: portalAbsorptionKeys.accountPackagesPrefix,
      });
    },
  });

  // Step 3 — sends the routed draft (send + invite, as the one-step path).
  const sendDraftMut = useMutation<CollectionPackageDto, Error, CollectionPackageDto>({
    mutationFn: async (draft) => {
      setSendProblems([]);
      const sent = await collectionPackagesApi.send(draft.id);
      await portalInvitationsApi.issue(draft.id, accountType as MaterialAudience);
      return sent;
    },
    onSuccess: (pkg) => {
      toast.success("Document request sent — invitation emailed.");
      invalidateApprovalChainReads(qc);
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
      void qc.invalidateQueries({
        queryKey: portalAbsorptionKeys.accountPackagesPrefix,
      });
      onSent?.(pkg);
      resetAndClose();
    },
    onError: (e) => {
      const problems = sendBlockedProblems(e);
      if (problems) setSendProblems(problems);
      else toast.error(extractErrorMessage(e));
      invalidateApprovalChainReads(qc);
    },
  });

  const routedItems = routingDraft
    ? routingDraft.items.filter(
        (i) =>
          i.approvalChainStatus != null || templateById.get(i.templateId)?.approvalChain != null,
      )
    : [];
  const routingReady = chainReadiness.summarize(routedItems.map((i) => i.id));

  const toggle = (setter: typeof setSelectedTemplateIds) => (id: string) =>
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleTemplate = toggle(setSelectedTemplateIds);
  const toggleBundle = toggle(setSelectedBundleIds);

  const footer =
    step === 1 ? (
      <>
        <Button
          type="button"
          variant="secondary"
          onClick={resetAndClose}
          disabled={sendMut.isPending}
        >
          Cancel
        </Button>
        {needsDetailsStep ? (
          <Button
            type="button"
            onClick={() => setStep(2)}
            disabled={templateIdsToCreate.length === 0}
            data-testid="new-request-continue"
          >
            Continue
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => sendMut.mutate({ routing: hasChainTemplate })}
            disabled={templateIdsToCreate.length === 0 || sendMut.isPending}
            loading={sendMut.isPending}
            data-testid="new-request-send"
          >
            <Send size={14} aria-hidden />{" "}
            {hasChainTemplate ? "Continue to routing" : "Send request"}
          </Button>
        )}
      </>
    ) : step === 3 && routingDraft ? (
      <>
        <Button
          type="button"
          variant="secondary"
          onClick={resetAndClose}
          disabled={sendDraftMut.isPending}
        >
          Close — keep as draft
        </Button>
        <Button
          type="button"
          onClick={() => sendDraftMut.mutate(routingDraft)}
          disabled={!routingReady.ready || sendDraftMut.isPending}
          loading={sendDraftMut.isPending}
          data-testid="new-request-routing-send"
        >
          <Send size={14} aria-hidden /> Send request
        </Button>
      </>
    ) : (
      <>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep(1)}
          disabled={sendMut.isPending}
          data-testid="new-request-back"
        >
          <ArrowLeft size={14} aria-hidden /> Back
        </Button>
        <Button
          type="button"
          onClick={() => sendMut.mutate({ routing: hasChainTemplate })}
          disabled={sendMut.isPending}
          loading={sendMut.isPending}
          data-testid="new-request-send"
        >
          <Send size={14} aria-hidden />{" "}
          {hasChainTemplate ? "Continue to routing" : "Send request"}
        </Button>
      </>
    );

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      title="New document request"
      description={
        step === 1
          ? "Pick the items to request; the package is created, sent, and the recipient invited in one step."
          : step === 3
            ? "Saved as a draft. Check who will receive each routed document, confirm what is required, then send."
            : "Confirm the pre-filled details for each document below, then send."
      }
      size={step === 1 ? "md" : "xl"}
      footer={footer}
    >
      {step === 1 ? (
        <div className="flex flex-col gap-space-4">
          {/* Universal search — reach off-audience templates/groups. When the
              box is non-empty the audience-scoped default lists are replaced by
              name-matched results from the WHOLE active catalog. */}
          <FormField label="Search all templates & groups" htmlFor="new-request-search">
            <div className="relative">
              <Input
                id="new-request-search"
                placeholder="Search across every audience…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                data-testid="new-request-search"
              />
              <Search
                size={14}
                className="absolute right-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
                aria-hidden
              />
            </div>
          </FormField>

          {searchActive ? (
            <fieldset
              className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 m-0 max-h-80 overflow-y-auto"
              data-testid="new-request-search-results"
            >
              <legend className="text-caption font-medium text-gray-mid px-space-2">
                Search results
              </legend>
              {searchTemplatesQuery.isLoading || searchBundlesQuery.isLoading ? (
                <p className="text-caption text-gray-mid m-0">Searching…</p>
              ) : searchBundleMatches.length === 0 &&
                searchTemplateMatches.length === 0 ? (
                <p className="text-caption text-gray-mid m-0 italic">
                  No templates or groups match “{search.trim()}”.
                </p>
              ) : (
                <>
                  {searchBundleMatches.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-start gap-space-2 text-small text-onyx m-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBundleIds.includes(b.id)}
                        onChange={() => toggleBundle(b.id)}
                      />
                      <span className="flex flex-col min-w-0">
                        <span className="font-medium truncate">{b.name}</span>
                        <span className="text-caption text-gray-mid">
                          Group · {b.slots.length} item
                          {b.slots.length === 1 ? "" : "s"}
                          {b.description ? ` · ${b.description}` : ""}
                        </span>
                      </span>
                    </label>
                  ))}
                  {searchTemplateMatches.map((t) => {
                    const aud = formatAudiences(t.audiences);
                    return (
                      <label
                        key={t.id}
                        className="flex items-start gap-space-2 text-small text-onyx m-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTemplateIds.includes(t.id)}
                          onChange={() => toggleTemplate(t.id)}
                        />
                        <TemplateKindBadge
                          kind={t.kind}
                          isSpecialForm={t.isSpecialForm}
                        />
                        <span className="flex flex-col min-w-0">
                          <span className="truncate">{t.name}</span>
                          <span
                            className="text-caption text-gray-mid truncate"
                            title={aud.title}
                          >
                            {aud.text}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </>
              )}
            </fieldset>
          ) : (
            <>
              <fieldset className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 m-0">
                <legend className="text-caption font-medium text-gray-mid px-space-2">
                  Groups (bundles)
                </legend>
                {bundlesQuery.isLoading ? (
                  <p className="text-caption text-gray-mid m-0">Loading bundles…</p>
                ) : bundles.length === 0 ? (
                  <p className="text-caption text-gray-mid m-0 italic">
                    No bundles available for this party type.
                  </p>
                ) : (
                  bundles.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-start gap-space-2 text-small text-onyx m-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBundleIds.includes(b.id)}
                        onChange={() => toggleBundle(b.id)}
                      />
                      <span className="flex flex-col">
                        <span className="font-medium">{b.name}</span>
                        <span className="text-caption text-gray-mid">
                          {b.slots.length} item{b.slots.length === 1 ? "" : "s"}
                          {b.description ? ` · ${b.description}` : ""}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </fieldset>

              <fieldset className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 m-0 max-h-64 overflow-y-auto">
                <legend className="text-caption font-medium text-gray-mid px-space-2">
                  Individual templates
                </legend>
                {templatesQuery.isLoading ? (
                  <p className="text-caption text-gray-mid m-0">Loading templates…</p>
                ) : templates.length === 0 ? (
                  <p className="text-caption text-gray-mid m-0 italic">
                    No templates available for this party type.
                  </p>
                ) : (
                  templates
                    .filter((t) => t.active)
                    .map((t) => (
                      <label
                        key={t.id}
                        className="flex items-center gap-space-2 text-small text-onyx m-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTemplateIds.includes(t.id)}
                          onChange={() => toggleTemplate(t.id)}
                        />
                        <TemplateKindBadge kind={t.kind} isSpecialForm={t.isSpecialForm} />
                        <span className="truncate">{t.name}</span>
                      </label>
                    ))
                )}
              </fieldset>
            </>
          )}

          <FormField label="Due date" htmlFor="new-request-due-date">
            <Input
              id="new-request-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </FormField>

          {needsDetailsStep && (
            <p className="text-caption text-gray-mid m-0">
              {detailTemplates.length} selected item
              {detailTemplates.length === 1 ? "" : "s"} need
              {detailTemplates.length === 1 ? "s" : ""} staff-entered details —
              the next step pre-fills and previews {detailTemplates.length === 1 ? "it" : "them"}.
            </p>
          )}

          {(templatesQuery.isError || bundlesQuery.isError) && (
            <p className="text-small text-status-error m-0">
              Could not load the template catalog — close and retry, or use the
              full composer.
            </p>
          )}
        </div>
      ) : step === 3 && routingDraft ? (
        <div className="flex flex-col gap-space-4" data-testid="new-request-routing">
          {routedItems.map((item) => (
            <ApprovalChainComposerPanel
              key={item.id}
              packageId={routingDraft.id}
              itemId={item.id}
              itemName={item.templateName || templateById.get(item.templateId)?.name || "this item"}
              dealId={routingDraft.ownerEntityType === "Deal" ? routingDraft.ownerEntityId : null}
              hasPermission={hasPermission}
              onReadinessChange={chainReadiness.report}
            />
          ))}
          <SendBlockedList
            title="Send is held until the approval chain is ready:"
            problems={routingReady.reasons}
            testId="chain-send-held"
          />
          <SendBlockedList
            title="The request was not sent. Fix each problem, then send again:"
            problems={sendProblems}
            testId="send-blocked-errors"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-space-6">
          {definitionsQuery.isLoading && specialFormIds.length > 0 ? (
            <p className="text-caption text-gray-mid m-0">
              Loading pre-filled details…
            </p>
          ) : (
            detailTemplates.map((t) => (
              <section key={t.id} className="flex flex-col gap-space-2">
                <h3 className="text-body font-semibold text-onyx m-0">{t.name}</h3>
                {t.isSpecialForm ? (
                  <SpecialFormEditor
                    mode="send"
                    templateId={t.id}
                    templateName={t.name}
                    dealId={ownerDealId}
                    formSchemaJson={t.formSchemaJson}
                    templateBody={
                      definitionsQuery.data?.[t.id]?.bodyTemplateMarkdown ??
                      t.bodyTemplateMarkdown ??
                      ""
                    }
                    bodyOverride={captures[t.id]?.bodyOverride ?? null}
                    onBodyOverrideChange={(body) => setCaptureBody(t.id, body)}
                    recipientName={account?.fullName ?? null}
                    staffName={user?.displayName ?? null}
                    staffEmail={user?.email ?? null}
                    onValuesChange={setCaptureValues}
                    initialValues={initialValuesFor(t.id)}
                  />
                ) : (
                  <SenderDetailsSection
                    templateId={t.id}
                    templateName={t.name}
                    formSchemaJson={t.formSchemaJson}
                    dealId={ownerDealId}
                    recipientName={account?.fullName ?? null}
                    staffName={user?.displayName ?? null}
                    staffEmail={user?.email ?? null}
                    onValuesChange={setCaptureValues}
                  />
                )}
              </section>
            ))
          )}
        </div>
      )}
    </Modal>
  );
}
